// src/lib/planFeatures.ts
//
// Unica fonte di verità sul perimetro dei piani di abbonamento.
//
// Piani in vendita:  pro (39) · ai (79) · enterprise (149, solo vetrina per ora)
// Piani legacy:      standard, visibility (prezzi Stripe archiviati, abbonati esistenti)
//
// Il piano arriva da AuthContext (subscriptionPlan) o da BillingContext
// (subscription.plan_id): entrambi derivano dalla lookup_key Stripe
// ({ciclo}_{slug}), quindi qui si ragiona solo sullo slug.

export type PlanId = 'pro' | 'ai' | 'enterprise' | 'standard' | 'visibility';

// Enterprise per ora offre le stesse funzionalità del piano AI (differisce solo
// per magazzino/multi-sede e servizi commerciali: onboarding, supporto dedicato).
const AI_TIER: string[] = ['ai', 'enterprise'];

/** Prenotazioni illimitate: tutti i piani nuovi. Il legacy standard resta a 200/mese. */
export const hasUnlimitedBookings = (plan?: string | null): boolean =>
    !!plan && ['pro', 'ai', 'enterprise'].includes(plan);

/** Assistente AI (pagina, widget flottante) e obiettivi: dal piano AI in su. */
export const hasAiFeatures = (plan?: string | null): boolean =>
    !!plan && AI_TIER.includes(plan);

/** Statistiche avanzate dipendenti (performance) e spesa per dipendente (compensi). */
export const hasStaffInsights = (plan?: string | null): boolean =>
    !!plan && AI_TIER.includes(plan);

/** Magazzino: dal piano AI in su. */
export const hasInventory = (plan?: string | null): boolean =>
    !!plan && AI_TIER.includes(plan);

/**
 * Numero massimo di collaboratori attivi (inviti pendenti inclusi).
 * null = nessun limite. Vale solo per i nuovi inviti: nessuna
 * disattivazione retroattiva di chi è già dentro.
 */
export const getStaffLimit = (plan?: string | null): number | null => {
    switch (plan) {
        case 'pro': return 3;
        case 'ai': return 10;
        case 'enterprise': return null; // illimitati
        case 'standard': return 2;      // limite storico del piano legacy
        default: return null;
    }
};

/**
 * In ambiente di test Stripe alcune lookup_key differiscono dalla produzione
 * (es. il prezzo del pro in test si chiama `annual_pro_2`). L'override si
 * configura in .env.local con un JSON, senza toccare il codice:
 *   VITE_STRIPE_LOOKUP_OVERRIDES={"annual_pro":"annual_pro_2"}
 */
const LOOKUP_OVERRIDES: Record<string, string> = (() => {
    try {
        return JSON.parse(import.meta.env.VITE_STRIPE_LOOKUP_OVERRIDES || '{}');
    } catch {
        console.error('VITE_STRIPE_LOOKUP_OVERRIDES non è un JSON valido: override ignorati');
        return {};
    }
})();

export const resolveStripeLookupKey = (key: string): string =>
    LOOKUP_OVERRIDES[key] ?? key;
